import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { LinkedProgram, ProblemDetail } from "shared";
import { api, ApiError } from "../api";
import { useCallerState } from "../callerState";
import { useLinkedCallerState } from "../linkedCallerState";
import { CopyButton } from "../components/CopyButton";
import { Field, isPending, PendingBadge, safeHref } from "../components/Field";
import { UrlRow } from "../components/ProgramView";
import { StatusBadge, VerificationBadge } from "../components/StatusBadge";
import { useProgramPanel } from "../programPanel";
import { STATES } from "../states";

export type ProblemSearch = { state?: string };

type Ticks = { slug: string; asked: number[]; collected: number[] };

function Checklist({ title, items, noun, done, setDone }:
  { title: string; items: string[]; noun: string; done: number[]; setDone: (d: number[]) => void }) {
  if (!items.length) return null;
  const toggle = (i: number) => setDone(done.includes(i) ? done.filter((x) => x !== i) : [...done, i]);
  return (
    <section className="card">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="text-xs text-slate-500">{done.length} of {items.length} {noun}</span>
      </div>
      <ul className="flex flex-col gap-1.5">{items.map((text, i) => (
        <li key={i}>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={done.includes(i)} onChange={() => toggle(i)} />
            <span className={done.includes(i) ? "text-slate-400 line-through" : ""}>{text}</span>
          </label>
        </li>
      ))}</ul>
      {done.length > 0 && <button type="button" className="btn-ghost mt-2 text-xs" onClick={() => setDone([])}>Clear ticks</button>}
    </section>
  );
}

/** state: the state the programs were loaded for, which a local name and a state version's verification belong to. */
function ProgramCard({ p, state, onOpen }: { p: LinkedProgram; state?: string; onOpen: () => void }) {
  return (
    <li className="card flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <button type="button" className="text-left font-medium text-brand-700 hover:underline dark:text-brand-100" onClick={onOpen}>{p.name}</button>
        <StatusBadge status={p.status} />
      </div>
      {p.localName && <div className="text-xs text-brand-700 dark:text-brand-100">{state ? `In ${state}: ` : ""}{p.localName}</div>}
      {p.reason && <p className="text-sm">{p.reason}</p>}
      {isPending(p.summary) ? <div className="text-sm text-slate-500">Summary: <PendingBadge /></div>
        : typeof p.summary === "string" && <p className="text-sm text-slate-600 dark:text-slate-400">{p.summary}</p>}
      <UrlRow label="Apply" value={p.applyUrl} />
      {p.phone && (isPending(p.phone)
        ? <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Phone</span><PendingBadge /></div>
        : <div className="flex items-center justify-between gap-2 text-sm"><span className="text-slate-500">{p.phone.label}</span>
            <span className="flex items-center gap-1 font-medium">{p.phone.value}<CopyButton text={p.phone.value} /></span></div>)}
      <div className="flex flex-wrap gap-2"><VerificationBadge v={p.verification} />
        {p.stateVerification && <span className="text-xs text-slate-500">{state ? `${state}: ` : ""}<VerificationBadge v={p.stateVerification} /></span>}</div>
    </li>
  );
}

export function ProblemPage() {
  const { slug } = useParams({ from: "/app/problems/$slug" });
  const search = useSearch({ from: "/app/problems/$slug" });
  const navigate = useNavigate({ from: "/problems/$slug" });
  const caller = useCallerState();
  const panel = useProgramPanel();
  // The top bar's caller state drives the page; a state in the link becomes the caller's state on arrival.
  const state = useLinkedCallerState(search.state,
    (s) => navigate({ search: (prev: ProblemSearch) => ({ ...prev, state: s }), replace: true }));
  // Ticks are for this call only; they are not saved. They live here, not in the checklists, so they survive the
  // caller's state changing (and a failed load of the new state's programs); another problem starts afresh.
  const [ticks, setTicks] = useState<Ticks>({ slug, asked: [], collected: [] });
  const mine = ticks.slug === slug ? ticks : { slug, asked: [], collected: [] };
  const q = useQuery({
    queryKey: ["problem", slug, state ?? ""],
    queryFn: () => api<ProblemDetail>(`/api/kb/problems/${encodeURIComponent(slug)}${state ? `?state=${state}` : ""}`),
    // While another state's programs load, the problem stays on screen; another problem never shows this one.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === slug ? prev : undefined),
  });
  if (!q.data) {
    if (q.isPending) return <p className="text-sm text-slate-500">Loading…</p>;
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <div role="alert" className="flex items-center gap-3 text-sm text-red-600">
        {missing ? "Problem not found." : "Could not load this problem."}
        {!missing && <button type="button" className="btn-ghost text-xs" onClick={() => void q.refetch()}>Try again</button>}
      </div>
    );
  }
  const d = q.data;
  // The programs on screen are for the state they were loaded for (the last one, while the next one loads).
  const shown = d.state ?? undefined;
  // The API lists offered programs, then the others, then discontinued ones; the page keeps that order.
  const offered = d.programs.filter((p) => p.available && p.status !== "discontinued");
  const elsewhere = d.programs.filter((p) => !p.available && p.status !== "discontinued");
  const ended = d.programs.filter((p) => p.status === "discontinued");
  // The page's select is the caller's state (the top bar shows the same); the link follows it. It is named
  // "State shown" so that only the top bar's control is "Caller's state".
  const chooseState = (v: string) => caller.setState(v || undefined);
  const card = (p: LinkedProgram) => <ProgramCard key={p.id} p={p} state={shown} onOpen={() => panel.open(p.slug, state)} />;

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-5">
      <div className="flex flex-col gap-4 lg:col-span-3">
        <div>
          <div className="text-xs text-slate-500"><Link to="/problems" className="hover:underline">Problems</Link>{d.category && <> / {d.category.name}</>}</div>
          <h1 className="text-xl font-semibold">{d.problem.title}</h1>
          {d.problem.summary && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{d.problem.summary}</p>}
          <div className="mt-1"><VerificationBadge v={d.problem.verification} /></div>
        </div>
        <Checklist title="Questions to ask" items={d.problem.questions} noun="asked"
          done={mine.asked} setDone={(asked) => setTicks({ ...mine, asked })} />
        <Checklist title="Information to collect" items={d.problem.infoNeeded} noun="collected"
          done={mine.collected} setDone={(collected) => setTicks({ ...mine, collected })} />
        <section className="card flex flex-col gap-4">
          <Field label="Guidance" value={d.problem.guidance} />
          <Field label="If no program fits" value={d.problem.alternatives} />
          <Field label="Follow-up" value={d.problem.followUp} />
        </section>
        {isPending(d.faqs) ? (
          <section className="card"><h2 className="mb-2 text-sm font-semibold">Common questions</h2><PendingBadge /></section>
        ) : d.faqs.length > 0 && (
          <section className="card flex flex-col gap-2"><h2 className="text-sm font-semibold">Common questions</h2>
            {d.faqs.map((f) => (
              <details key={f.id} className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
                <summary className="cursor-pointer text-sm font-medium">{f.question}</summary>
                <p className="mt-1 whitespace-pre-line text-sm">{f.answer}</p>
              </details>
            ))}
          </section>
        )}
        {d.sources.length > 0 && (
          <section className="card"><h2 className="mb-2 text-sm font-semibold">Sources</h2>
            <ul className="text-xs">{d.sources.map((s) => (
              <li key={s.url}>{safeHref(s.url) ? <a href={s.url} target="_blank" rel="noreferrer noopener" className="text-brand-700 hover:underline dark:text-brand-100">{s.title ?? s.url}</a> : <span>{s.title ?? s.url}</span>}{s.publisher && ` · ${s.publisher}`}</li>
            ))}</ul>
          </section>
        )}
      </div>

      <aside className="flex flex-col gap-3 lg:col-span-2">
        <section className="card">
          <label className="label" htmlFor="problem-state">State shown</label>
          <select id="problem-state" className="input" value={state ?? ""} onChange={(e) => chooseState(e.target.value)}>
            <option value="">Any state</option>
            {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </section>
        <h2 className="text-sm font-semibold">{shown ? `Programs to check in ${shown}` : "Programs to check"}</h2>
        {q.isPlaceholderData && <p className="text-xs text-slate-500">Loading programs{state ? ` for ${state}` : ""}…</p>}
        {offered.length === 0 && <p className="text-sm text-slate-500">No linked programs{shown ? ` offered in ${shown}` : ""}.</p>}
        <ul className="flex flex-col gap-3">{offered.map(card)}</ul>
        {elsewhere.length > 0 && (
          <details className="card">
            <summary className="cursor-pointer text-sm font-medium">Not offered in {shown} ({elsewhere.length})</summary>
            <ul className="mt-2 flex flex-col gap-3">{elsewhere.map(card)}</ul>
          </details>
        )}
        {ended.length > 0 && (
          <section aria-label="Ended programs" className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Ended programs ({ended.length})</h3>
            <ul className="flex flex-col gap-3">{ended.map(card)}</ul>
          </section>
        )}
      </aside>
    </div>
  );
}
